<script lang="ts">
	/**
	 * Activity Log filters (event type + room) for mobile/tablet, opened from the
	 * list header kebab. Desktop keeps the same controls inline in the Toolbar.
	 * Edits are held locally and committed on Apply, mirroring GroupsFilterModal.
	 */
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	// Types
	import type { ActivityLogFilters } from 'src/stores/ui/filters';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	// Stores
	import { roomsActivityLogFilter, getSelectedActivityLogFilterCount } from 'src/stores/ui/filters';
	import { rooms } from 'src/stores/lists/rooms';
	// Components
	import { sortBy } from 'src/components/Lists/list-helpers';
	// Design system components
	import { Container, Modal, Text, Button, IconButton, Select } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import { defaultHiddenDevicesRoom, defaultSelectedRoom } from 'src/helpers/constants';

	export let openFilterModal = false;
	export let typeOptions: SelectOptionPropsType[] = [];
	export let selectedTypeValue = 'all';

	// `roomID` is `unknown` because the design system types option values that
	// way; room ids are strings or numbers depending on the option.
	const dispatch = createEventDispatcher<{
		apply: { type: string; roomID: unknown };
	}>();

	let appliedFilters: ActivityLogFilters = deepCopy($roomsActivityLogFilter);
	let appliedTypeValue = selectedTypeValue;

	$: orderedRooms = Object.values($rooms)
		.sort((x, y) => {
			return sortBy(x, y, 'position');
		})
		.map((room: any) => {
			return {
				title: room.name,
				value: room.id,
			};
		});

	$: roomsOptions = [
		{
			...defaultSelectedRoom,
			title: $format(defaultSelectedRoom.title, {
				default: defaultSelectedRoom.translationDefault,
			}),
		},
		...(orderedRooms && [...orderedRooms]),
		{
			...defaultHiddenDevicesRoom,
			title: $format(defaultHiddenDevicesRoom.title, {
				default: defaultHiddenDevicesRoom.translationDefault,
			}),
		},
	];

	$: selectedRoomValue =
		appliedFilters.rooms.find((filter) => filter.value)?.value || defaultSelectedRoom.value;

	$: selectedRoom =
		roomsOptions.find((option) => option.value === selectedRoomValue) || roomsOptions[0];

	$: selectedType =
		typeOptions.find((option) => option.value === appliedTypeValue) || typeOptions[0];

	$: notification = getSelectedActivityLogFilterCount(appliedFilters, appliedTypeValue);

	const handleTypeChange = (e: CustomEvent) => {
		appliedTypeValue = e.detail.value;
	};

	const handleRoomChange = (e: CustomEvent) => {
		appliedFilters.rooms = [e.detail];
	};

	const handleFilter = () => {
		roomsActivityLogFilter.set(appliedFilters);
		dispatch('apply', { type: appliedTypeValue, roomID: selectedRoomValue });
		openFilterModal = false;
	};

	const resetData = () => {
		appliedFilters = { rooms: [defaultSelectedRoom] };
		appliedTypeValue = 'all';
	};
</script>

<Modal
	title={$format('label_filter', { default: 'Filter' })}
	showModal={openFilterModal}
	on:close={() => {
		openFilterModal = false;
	}}
	backdropClose
	{notification}
	removeScrollPadding
>
	<Container fullHeight slot="content" gap="s" padding="scrollPaddings">
		<Text class="mb-s" element="h2">
			{$format('label_filter_by_type', { default: 'Filter by type' })}
		</Text>
		<Select
			class="width-sm-full width-md-l mb-m"
			id="select-type-filter-eventlog"
			aria-labelledby="select-type-filter-eventlog"
			value={selectedType}
			on:change={handleTypeChange}
			options={typeOptions}
			icon="fas fa-filter"
		/>

		<Text class="mb-s" element="h2">
			{$format('all_rooms', { default: 'All rooms' })}
		</Text>
		<Select
			class="width-sm-full width-md-l"
			id="select-room-filter-eventlog"
			aria-labelledby="select-room-filter-eventlog"
			value={selectedRoom}
			on:change={handleRoomChange}
			options={roomsOptions}
			icon="fas fa-door-open"
			label={$format('all_rooms', { default: 'All rooms' })}
		/>
	</Container>
	<div slot="footer" class="d-flex flex-direction-column">
		<IconButton
			on:click={resetData}
			icon="fas fa-xmark"
			variant="secondary"
			class="width-full"
			disabled={!notification}
		>
			{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
		</IconButton>
		<div class="d-flex justify-content-end mt-md-l mt-sm-m">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (openFilterModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleFilter}
			>
				{capFirstChar($format('button_apply', { default: 'Apply' }))}
			</Button>
		</div>
	</div>
</Modal>
